import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api/index.ts";
import { User } from "@/lib/api/user.ts";
import { DateTime } from "luxon";
import { getDeviceTimezone } from "@/lib/utils/timezone";

export interface Session {
  id: string;
  mentor: User;
  mentee: User;
  host: User;
  status: string;
  note: string;
  title: string;
  scheduled_time: string;
  created_at: string;
  updated_at: string;
  metadata: Record<string, any>;
}

export const useSessions = (menteeId?: string) => {
  return useQuery<Session[]>({
    queryKey: ["sessions", menteeId],
    queryFn: async () => {
      const sessions = await fetchApi<{ sessions: Session[] }>({
        url: `/v2/sessions/${menteeId}`,
        method: "GET",
      });

      if (!sessions.data?.sessions) throw new Error("No Sessions found");

      return sessions.data.sessions;
    },
  });
};

export const useGetAvailableDays = () => {
  return useQuery({
    queryKey: ["available-days"],
    queryFn: async () => {
      const { data } = await fetchApi<{ dates: string[] }>({
        url: `/v2/sessions/days`,
        method: "GET",
      });

      const dates =
        data?.dates?.map((date) => {
          return DateTime.fromISO(new Date(date).toISOString(), {
            zone: "utc",
          })
            .setZone(getDeviceTimezone())
            .set({
              minute: 0,
              hour: 0,
              second: 0,
              millisecond: 0,
            });
        }) || [];

      const daysInMonth = DateTime.local().daysInMonth;

      const disabledDates: Date[] = [];

      for (let i = 1; i <= daysInMonth; i++) {
        if (
          dates.some((date) =>
            date.hasSame(DateTime.local().set({ day: i }), "day"),
          )
        ) {
          disabledDates.push(DateTime.local().set({ day: i }).toJSDate());
        }
      }

      return disabledDates;
    },
  });
};

interface ScheduleSessionParams {
  scheduled_time: string;
  note?: string;
  mentee_id?: string;
  timezone?: string;
}

export const useScheduleSession = (menteeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["schedule-session", menteeId],
    mutationFn: async (data: ScheduleSessionParams) => {
      const session = await fetchApi<{ session: Session }>({
        url: `/v2/sessions`,
        method: "POST",
        data,
      });

      return session.data.session;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["sessions", menteeId],
      });
    },
  });
};

interface RescheduleSessionParams extends ScheduleSessionParams {
  session_id: string;
}

export const useRescheduleSession = (menteeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["reschedule-session", menteeId],
    mutationFn: async (data: RescheduleSessionParams) => {
      const session = await fetchApi<{ session: Session }>({
        url: `/v2/sessions/${data.session_id}`,
        method: "PATCH",
        data,
      });

      return session.data.session;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["sessions", menteeId],
      });
    },
  });
};

interface CancelSessionParams {
  session_id: string;
}

export const useCancelSession = (menteeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["cancel-session", menteeId],
    mutationFn: async (data: CancelSessionParams) => {
      await fetchApi<{ session: Session }>({
        url: `/v2/sessions/${data.session_id}`,
        method: "DELETE",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["sessions", menteeId],
      });
    },
  });
};
